import React from "react";
import PropTypes from "prop-types";

import { Alert, Button, Card, Col, Descriptions, Input, Radio, Row, Select, Space, Spin, Tag, Typography } from "antd";

import { CarOutlined, EnvironmentOutlined, ShopOutlined } from "@ant-design/icons";

import {
    COUNTRY_LABELS,
    DELIVERY_TYPE_LABELS,
    DELIVERY_TYPE_ORDER,
    SERVICE_TYPE_LABELS,
    formatAmount,
    formatDate,
    unavailableReasonLabel
} from "../../../../../../constants/marketplaceCheckout";

const { Text, Title } = Typography;

const ICONS = {
    DELIVERY_BY_BUYER: <ShopOutlined />,
    DELIVERY_BY_PLATFORM: <EnvironmentOutlined />,
    DELIVERY_BY_EXTERNAL: <CarOutlined />
};

export const EMPTY_ADDRESS = {
    country: null, city: "", district: "", street: "", landmark: "", additionalInformation: "", contactPhone: ""
};

/** Pays, ville, quartier et rue obligatoires ; repère, informations complémentaires et téléphone facultatifs. */
export const missingAddressFields = address => {
    const missing = [];
    if (!address?.country) missing.push("pays");
    if (!address?.city?.trim()) missing.push("ville");
    if (!address?.district?.trim()) missing.push("quartier");
    if (!address?.street?.trim()) missing.push("rue / indication");
    return missing;
};

export const requiresAddress = deliveryType =>
    deliveryType === "DELIVERY_BY_PLATFORM" || deliveryType === "DELIVERY_BY_EXTERNAL";

/**
 * Section « Livraison » du parcours de paiement.
 * Les options et montants viennent du backend (devis) ; rien n'est calculé ici.
 */
const DeliverySection = ({
    deliveryType,
    onDeliveryTypeChange,
    address,
    onAddressChange,
    quotes,
    quotesLoading,
    quotesError,
    onRequestQuotes,
    selectedOptionId,
    onSelectOption,
    disabled
}) => {
    const needsAddress = requiresAddress(deliveryType);
    const missing = missingAddressFields(address);
    const options = quotes?.options ?? [];
    const setField = field => event =>
        onAddressChange({ ...address, [field]: event?.target ? event.target.value : event });

    return (
        <Card>
            <Title level={5} style={{ marginTop: 0 }}>1. Livraison</Title>

            <Radio.Group
                value={deliveryType}
                disabled={disabled}
                onChange={event => onDeliveryTypeChange(event.target.value)}
                style={{ width: "100%" }}
            >
                <Space orientation="vertical" style={{ width: "100%" }}>
                    {DELIVERY_TYPE_ORDER.map(type => (
                        <Radio key={type} value={type}>
                            <Space align="start">
                                {ICONS[type]}
                                <span>
                                    <strong>{DELIVERY_TYPE_LABELS[type].title}</strong>
                                    <br />
                                    <Text type="secondary">{DELIVERY_TYPE_LABELS[type].description}</Text>
                                </span>
                            </Space>
                        </Radio>
                    ))}
                </Space>
            </Radio.Group>

            {deliveryType === "DELIVERY_BY_BUYER" && (
                <div style={{ marginTop: 16 }}>
                    {quotesLoading && <Spin description="Chargement du point de retrait..." />}
                    {quotes?.pickupPoint ? (
                        <Descriptions size="small" column={1} bordered title="Point de retrait">
                            <Descriptions.Item label="Lieu">{quotes.pickupPoint.name}</Descriptions.Item>
                            <Descriptions.Item label="Adresse">
                                {[quotes.pickupPoint.address, quotes.pickupPoint.city].filter(Boolean).join(", ")}
                            </Descriptions.Item>
                            {quotes.pickupPoint.openingHours && (
                                <Descriptions.Item label="Horaires">{quotes.pickupPoint.openingHours}</Descriptions.Item>
                            )}
                            {quotes.pickupPoint.instructions && (
                                <Descriptions.Item label="Instructions">{quotes.pickupPoint.instructions}</Descriptions.Item>
                            )}
                            {quotes.pickupPoint.phone && (
                                <Descriptions.Item label="Contact">{quotes.pickupPoint.phone}</Descriptions.Item>
                            )}
                        </Descriptions>
                    ) : (!quotesLoading && quotes && (
                        <Alert
                            type="info"
                            showIcon
                            title="Le point de retrait vous sera communiqué par ASSIGANTO après le paiement."
                        />
                    ))}
                </div>
            )}

            {needsAddress && (
                <div style={{ marginTop: 16 }}>
                    <Title level={5}>Adresse de livraison</Title>
                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12}>
                            <Text>Pays</Text>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="Pays"
                                value={address.country}
                                disabled={disabled}
                                onChange={setField("country")}
                                options={Object.entries(COUNTRY_LABELS).map(([value, label]) => ({ value, label }))}
                            />
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text>Ville</Text>
                            <Input placeholder="Ex. Lomé" value={address.city} disabled={disabled} onChange={setField("city")} />
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text>Quartier</Text>
                            <Input placeholder="Ex. Agoè" value={address.district} disabled={disabled} onChange={setField("district")} />
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text>Rue / indication</Text>
                            <Input placeholder="Rue, numéro, bâtiment" value={address.street} disabled={disabled} onChange={setField("street")} />
                        </Col>
                        <Col span={24}>
                            <Text>Point de proximité (recommandé)</Text>
                            <Input
                                placeholder="Ex. près de la pharmacie X, face au marché Y"
                                value={address.landmark}
                                disabled={disabled}
                                onChange={setField("landmark")}
                            />
                        </Col>
                        <Col xs={24} sm={14}>
                            <Text>Informations complémentaires</Text>
                            <Input
                                placeholder="Ex. portail bleu, 2e étage"
                                value={address.additionalInformation}
                                disabled={disabled}
                                onChange={setField("additionalInformation")}
                            />
                        </Col>
                        <Col xs={24} sm={10}>
                            <Text>Téléphone de livraison (facultatif)</Text>
                            <Input
                                inputMode="tel"
                                placeholder="Ex. +228 90 12 34 56"
                                value={address.contactPhone}
                                disabled={disabled}
                                onChange={setField("contactPhone")}
                            />
                        </Col>
                    </Row>

                    <Button
                        style={{ marginTop: 12 }}
                        onClick={onRequestQuotes}
                        disabled={disabled || missing.length > 0}
                        loading={quotesLoading}
                    >
                        {deliveryType === "DELIVERY_BY_PLATFORM" ? "Calculer les frais de livraison" : "Voir les partenaires disponibles"}
                    </Button>
                    {missing.length > 0 && (
                        <div><Text type="secondary">À compléter : {missing.join(", ")}.</Text></div>
                    )}
                </div>
            )}

            {quotesError && <Alert style={{ marginTop: 16 }} type="error" showIcon title={quotesError} />}

            {needsAddress && quotes?.unavailableReason && (
                <Alert style={{ marginTop: 16 }} type="warning" showIcon title={unavailableReasonLabel(quotes.unavailableReason)} />
            )}

            {needsAddress && options.length > 0 && (
                <Radio.Group
                    style={{ width: "100%", marginTop: 16 }}
                    value={selectedOptionId}
                    disabled={disabled}
                    onChange={event => onSelectOption(event.target.value)}
                >
                    <Space orientation="vertical" style={{ width: "100%" }}>
                        {options.map(option => (
                            <Radio key={option.id} value={option.id}>
                                <strong>{option.label || option.providerName}</strong>{" "}
                                <Tag color="green">{formatAmount(option.deliveryCost, option.currency)}</Tag>
                                {option.serviceType && <Tag>{SERVICE_TYPE_LABELS[option.serviceType] || option.serviceType}</Tag>}
                                {option.providerMode === "MOCK" && <Tag color="orange">Simulé</Tag>}
                                <Text type="secondary">Livraison estimée : {formatDate(option.estimatedDeliveryDate)}</Text>
                            </Radio>
                        ))}
                    </Space>
                </Radio.Group>
            )}
        </Card>
    );
};

DeliverySection.propTypes = {
    deliveryType: PropTypes.string,
    onDeliveryTypeChange: PropTypes.func.isRequired,
    address: PropTypes.shape({
        country: PropTypes.string,
        city: PropTypes.string,
        district: PropTypes.string,
        street: PropTypes.string,
        landmark: PropTypes.string,
        additionalInformation: PropTypes.string,
        contactPhone: PropTypes.string
    }).isRequired,
    onAddressChange: PropTypes.func.isRequired,
    quotes: PropTypes.object,
    quotesLoading: PropTypes.bool,
    quotesError: PropTypes.string,
    onRequestQuotes: PropTypes.func.isRequired,
    selectedOptionId: PropTypes.string,
    onSelectOption: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};

export default DeliverySection;
